// ============================================================================
// CASE GROUP DETAIL MODAL (XL, left-menu shell) — Phase A
// ============================================================================
// Mirrors the litigation case-detail shell. Sections: Summary (header), Members,
// Notices (A4), Results (A5), Settlement (A6), Workflow Timeline. Reuses the
// case-group form modal (edit header) and candidate picker (add members).

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useCaseGroup } from 'hooks/api/useCaseGroups';
import { useCaseGroupExpenses } from 'hooks/api/useExpenses';
import { caseGroupService } from 'api/services/caseGroupService';
import type { CaseGroup, CaseGroupMember, MemberStateSnapshot, NoticeRecipient, PreSaleLetter } from 'api/types/caseGroup.types';
import type { ExpenseAllocationView } from 'api/types/expense.types';
import CaseGroupFormModal from './CaseGroupFormModal';
import CaseGroupCandidatePickerModal from './CaseGroupCandidatePickerModal';
import ExpenseFormModal from 'components/modals/modules/expense-module/ExpenseFormModal';

export interface CaseGroupDetailModalProps {
  isOpen: boolean;
  groupId: string | null;
  onClose: () => void;
}

// Fixed (cross-cutting) menu items. The Process group between them is built
// dynamically from group.workflowStates so the same shell works for AUCTION,
// ADALATH, OTS, conciliation, etc. without code changes.
type SectionCode = string;

interface NavItem { code: SectionCode; label: string; icon: keyof typeof IconSystem; }
interface NavGroup { group: string; items: NavItem[]; }

const FIXED_NAV_PRE: NavGroup[] = [
  { group: 'Overview', items: [{ code: 'summary', label: 'Summary', icon: 'file' }] },
  { group: 'Members',  items: [{ code: 'members', label: 'Members', icon: 'tag' }] },
];
const FIXED_NAV_POST: NavGroup[] = [
  { group: 'Financial', items: [{ code: 'expenses', label: 'Expenses', icon: 'dollarSign' }] },
  { group: 'Workflow',  items: [{ code: 'timeline', label: 'Workflow Timeline', icon: 'clock' }] },
];

// Vehicle-auction stages (extend with per-group_type map as more workflows are seeded).
const AUCTION_STAGES: { code: string; label: string }[] = [
  { code: 'DRAFT',                label: 'Assemble Members' },
  { code: 'VALUATION',            label: 'Valuation' },
  { code: 'PRE_SALE_NOTICE_SENT', label: 'Pre-Sale Notice' },
  { code: 'NEWSPAPER_PUBLISHED',  label: 'Newspaper Published' },
  { code: 'AUCTION_CONDUCTED',    label: 'Auction Conducted' },
  { code: 'AUCTION_SETTLED',      label: 'Settled' },
  { code: 'COMPLETED',            label: 'Completed' },
];

const fmtDate = (s?: string | null) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; } };

/**
 * Convert a server ISO timestamp (UTC) to YYYY-MM-DD in the *local* timezone,
 * suitable for prefilling `<input type="date">`. A naïve `.slice(0, 10)` on the
 * ISO string returns the UTC calendar date, which is wrong for IST users when
 * the server timestamp is just before midnight IST (= late evening UTC).
 */
const toLocalDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const CaseGroupDetailModal: React.FC<CaseGroupDetailModalProps> = ({ isOpen, groupId, onClose }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: group, loading } = useCaseGroup(groupId ?? undefined, refreshKey);
  const [active, setActive] = useState<SectionCode>('summary');
  const [editOpen, setEditOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<CaseGroupMember | null>(null);
  const [removing, setRemoving] = useState(false);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const handleRemoveMember = useCallback(async () => {
    if (!memberToRemove) return;
    setRemoving(true);
    try {
      await caseGroupService.removeMember(memberToRemove.id);
      setRemoving(false); setMemberToRemove(null);
      notificationService.toast.success('Member removed.');
      refresh();
    } catch (err: any) {
      setRemoving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to remove member.', 6000);
    }
  }, [memberToRemove, refresh]);

  const members = group?.members ?? [];
  const navCountLabel = useCallback((code: SectionCode) => (code === 'members' && members.length ? ` (${members.length})` : ''), [members.length]);

  // Dynamic Process menu — one entry per workflow state that has a form_spec.
  // Falls back to nothing if the group's workflow definition isn't loaded yet.
  const dynamicNav = useMemo<NavGroup[]>(() => {
    const processItems: NavItem[] = (group?.workflowStates ?? [])
      .filter(s => s.hasFormSpec)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(s => {
        const iconKey = (s.panelIcon && (s.panelIcon in IconSystem))
          ? (s.panelIcon as keyof typeof IconSystem)
          : ('check' as keyof typeof IconSystem);
        return {
          code: s.stateCode,
          label: s.panelTitle || s.displayName,
          icon: iconKey,
        };
      });
    const processGroup: NavGroup[] = processItems.length > 0
      ? [{ group: 'Process', items: processItems }]
      : [];
    return [...FIXED_NAV_PRE, ...processGroup, ...FIXED_NAV_POST];
  }, [group?.workflowStates]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {group ? `${group.groupType.replace('_', ' ')} · ${group.groupCode}` : 'Case Group'}
            {group && (
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,0.12)', padding: '2px 10px', borderRadius: 999 }}>
                {group.currentStateCode || 'DRAFT'}
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <button className="modal-header-btn" onClick={refresh} title="Refresh"><IconSystem.refresh size={16} /></button>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        {/* Identity strip */}
        {group && (
          <div style={{
            padding: '0.4rem 1.25rem', borderBottom: '1px solid var(--border-color)',
            fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>{group.title || group.groupType}</strong>
            {group.groupSubtype ? ` · ${group.groupSubtype}` : ''}
            {' · '}{group.mode}
            {' · '}{fmtDate(group.eventDate)}
            {group.venue ? ` · ${group.venue}` : ''}
            {' · '}{members.length} member{members.length === 1 ? '' : 's'}
          </div>
        )}

        {/* Two-pane: sidebar + content */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Sidebar */}
          <aside style={{
            width: 210, flexShrink: 0, borderRight: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)', overflowY: 'auto', padding: '0.5rem 0',
          }}>
            {dynamicNav.map(g => (
              <div key={g.group} style={{ marginBottom: '0.4rem' }}>
                <div style={{ padding: '0.35rem 0.85rem 0.2rem', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700 }}>
                  {g.group}
                </div>
                {g.items.map(it => {
                  const Icon = IconSystem[it.icon] as React.FC<any>;
                  const on = active === it.code;
                  return (
                    <button key={it.code} type="button" onClick={() => setActive(it.code)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                        padding: '0.45rem 0.85rem', border: 'none', cursor: 'pointer', textAlign: 'left',
                        fontSize: '0.83rem', fontWeight: on ? 600 : 400,
                        color: on ? '#2563eb' : 'var(--text-primary)',
                        background: on ? 'var(--bg-primary)' : 'transparent',
                        borderLeft: on ? '3px solid #2563eb' : '3px solid transparent',
                      }}>
                      {Icon && <Icon size={15} />}
                      {it.label}{navCountLabel(it.code)}
                    </button>
                  );
                })}
              </div>
            ))}
          </aside>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
            {loading && !group ? (
              <div style={{ color: 'var(--text-secondary)' }}>Loading case-group…</div>
            ) : !group ? (
              <div style={{ color: 'var(--text-muted)' }}>Case-group not found.</div>
            ) : active === 'summary' ? (
              <SummaryView group={group} onEdit={() => setEditOpen(true)} />
            ) : active === 'members' ? (
              <MembersView groupId={group.id} members={members} onAdd={() => setPickerOpen(true)} onRemove={setMemberToRemove} />
            ) : active === 'VALUATION' && group.groupType === 'AUCTION' ? (
              <ValuationView group={group} onSaved={refresh} />
            ) : active === 'PRE_SALE_NOTICE_SENT' && group.groupType === 'AUCTION' ? (
              <PreSaleNoticesView group={group} onSaved={refresh} />
            ) : active === 'NEWSPAPER_PUBLISHED' && group.groupType === 'AUCTION' ? (
              <NewspaperView group={group} onSaved={refresh} />
            ) : active === 'AUCTION_CONDUCTED' && group.groupType === 'AUCTION' ? (
              <ConductResultsView group={group} onSaved={refresh} />
            ) : active === 'AUCTION_SETTLED' && group.groupType === 'AUCTION' ? (
              <SettlementView group={group} onSaved={refresh} />
            ) : active === 'expenses' ? (
              <GroupExpensesView group={group} />
            ) : active === 'timeline' ? (
              <TimelineView currentState={group.currentStateCode} />
            ) : (
              <Placeholder section={active} workflowStates={group.workflowStates} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>

      {group && (
        <>
          <CaseGroupFormModal isOpen={editOpen} mode="edit" record={group} onSaved={refresh} onClose={() => setEditOpen(false)} />
          <CaseGroupCandidatePickerModal isOpen={pickerOpen} groupId={group.id} onAdded={refresh} onClose={() => setPickerOpen(false)} />
        </>
      )}

      <ConfirmDialog
        open={!!memberToRemove}
        title="Remove member?"
        message={memberToRemove ? `Remove case ${memberToRemove.caseCode || ''} from this event?` : ''}
        confirmLabel="Remove" cancelLabel="Cancel" variant="danger"
        loading={removing} loadingMessage="Removing…"
        onConfirm={handleRemoveMember}
        onCancel={() => { if (!removing) setMemberToRemove(null); }}
      />
    </>
  );
};

// ----------------------------------------------------------------------------
// Section views
// ----------------------------------------------------------------------------

const SummaryView: React.FC<{ group: CaseGroup; onEdit: () => void }> = ({ group, onEdit }) => {
  const s = group.summary;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Event Details</h3>
        <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem' }} onClick={onEdit}>
          <IconSystem.edit size={14} style={{ marginRight: '0.3rem' }} /> Edit details
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.85rem' }}>
        {[
          ['Group Type',  group.groupType.replace('_', ' ')],
          ['Subtype',     group.groupSubtype || '—'],
          ['Mode',        group.mode],
          ['Event Date',  fmtDate(group.eventDate)],
          ['Venue',       group.venue || '—'],
          ['Platform',    group.platform || '—'],
          ['Title',       group.title || '—'],
        ].map(([label, value]) => (
          <div key={label as string}>
            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-muted)' }}>{label}</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '0.1rem' }}>{value}</div>
          </div>
        ))}
      </div>
      {group.description && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-muted)' }}>Remarks</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginTop: '0.2rem', whiteSpace: 'pre-wrap' }}>{group.description}</div>
        </div>
      )}

      {/* Live analytics — from lit.vw_case_group_summary */}
      {s && (
        <div style={{ marginTop: '1.25rem', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 0.85rem', background: 'var(--bg-secondary)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <IconSystem.dollarSign size={13} /> Live Analytics
          </div>
          <div style={{ padding: '0.85rem 1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.85rem' }}>
            <Stat label="Members" value={
              <span>
                <strong>{s.membersIncluded}</strong> included
                {s.membersWithdrawn > 0 && <span style={{ color: '#dc2626' }}> · {s.membersWithdrawn} withdrawn</span>}
                {s.membersDeferred  > 0 && <span style={{ color: '#d97706' }}> · {s.membersDeferred} deferred</span>}
              </span>
            } />
            <Stat label="Demand"    value={<strong>{fmtAmount(s.demandAmount)}</strong>} />
            <Stat label="Recovery"  value={<strong style={{ color: s.recoveryAchieved > 0 ? '#16a34a' : 'var(--text-primary)' }}>{fmtAmount(s.recoveryAchieved)}</strong>} />
            <Stat label="Recovery %" value={
              <strong style={{ color: s.recoveryPercentage >= 70 ? '#16a34a' : s.recoveryPercentage >= 40 ? '#d97706' : '#dc2626' }}>
                {Number(s.recoveryPercentage).toFixed(2)}%
              </strong>
            } />
          </div>
          {s.demandAmount > 0 && (
            <div style={{ height: 6, background: 'var(--bg-secondary)', position: 'relative' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${Math.min(100, Number(s.recoveryPercentage) || 0)}%`,
                background: s.recoveryPercentage >= 70 ? '#16a34a' : s.recoveryPercentage >= 40 ? '#d97706' : '#2563eb',
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>{label}</div>
    <div style={{ fontSize: '0.92rem', color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

// Fan-out stages that produce per-case state_data (excludes DRAFT/COMPLETED bookkeeping states).
// For the first cut this is the auction workflow's set; extend per group_type later.
const FANOUT_STAGES: { code: string; short: string }[] = [
  { code: 'VALUATION',            short: 'Valued'    },
  { code: 'PRE_SALE_NOTICE_SENT', short: 'Notice'    },
  { code: 'NEWSPAPER_PUBLISHED',  short: 'Published' },
  { code: 'AUCTION_CONDUCTED',    short: 'Held'      },
  { code: 'AUCTION_SETTLED',      short: 'Settled'   },
];

const fmtAmount = (n?: number | null) => (n == null ? '' : `₹${Number(n).toLocaleString('en-IN')}`);

const stagePill = (snap?: MemberStateSnapshot) => {
  if (!snap) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  // Summary depends on which fields the snapshot carries.
  let label = '✓';
  if (snap.statusValue) label = `${snap.statusValue === 'DELIVERED' || snap.statusValue === 'SOLD' ? '✓' : snap.statusValue === 'RETURNED' || snap.statusValue === 'UNSOLD' ? '⚠' : '·'} ${snap.statusValue}`;
  else if (snap.amountPrimary != null) label = `✓ ${fmtAmount(snap.amountPrimary)}`;
  const isWarning = snap.statusValue === 'RETURNED' || snap.statusValue === 'UNSOLD' || snap.statusValue === 'REFUSED';
  const color = isWarning ? '#d97706' : '#16a34a';
  return (
    <span title={[
      snap.eventDate ? `Event: ${fmtDate(snap.eventDate)}` : null,
      snap.amountPrimary != null ? `Amount: ${fmtAmount(snap.amountPrimary)}` : null,
      snap.counterpartyName ? `Counterparty: ${snap.counterpartyName}` : null,
      snap.refMasterName ? `Ref: ${snap.refMasterName}` : null,
      snap.submittedByName ? `By ${snap.submittedByName}` : null,
    ].filter(Boolean).join(' · ')}
      style={{ fontSize: '0.72rem', fontWeight: 600, color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
};

const MembersView: React.FC<{ groupId: string; members: CaseGroupMember[]; onAdd: () => void; onRemove: (m: CaseGroupMember) => void }> = ({ groupId, members, onAdd, onRemove }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Members ({members.length})</h3>
        <button className="btn btn-primary" style={{ padding: '0.3rem 0.7rem' }} onClick={onAdd}>
          <IconSystem.add size={14} style={{ marginRight: '0.3rem' }} /> Add cases
        </button>
      </div>
      {members.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No members yet. Click "Add cases" to include eligible cases.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={th}></th>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Customer</th>
                <th style={th}>Asset</th>
                {FANOUT_STAGES.map(s => <th key={s.code} style={th}>{s.short}</th>)}
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const sd = m.stateData ?? {};
                const isOpen = expanded.has(m.id);
                return (
                  <React.Fragment key={m.id}>
                    <tr onClick={() => toggle(m.id)} style={{ cursor: 'pointer', background: isOpen ? 'rgba(37,99,235,0.04)' : 'transparent' }}>
                      <td style={cell}>
                        <IconSystem.chevronRight size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text-muted)' }} />
                      </td>
                      <td style={cell}>{m.seqNo ?? '—'}</td>
                      <td style={cell}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                          <strong>{m.caseCode || '—'}</strong>
                          {m.parentCaseCode && (
                            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }} title="Spawned from this repo case">
                              ← {m.parentCaseCode}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={cell}>{m.customerName || '—'}</td>
                      <td style={cell}>{m.vehicleRegistrationNo || '—'}</td>
                      {FANOUT_STAGES.map(s => <td key={s.code} style={cell}>{stagePill(sd[s.code])}</td>)}
                      <td style={cell}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: m.participationStatus === 'WITHDRAWN' ? '#dc2626' : m.participationStatus === 'DEFERRED' ? '#d97706' : '#16a34a',
                                        border: '1px solid currentColor', borderRadius: 999, padding: '1px 7px' }}>
                          {m.participationStatus}
                        </span>
                      </td>
                      <td style={cell}>
                        <button type="button" title="Remove member" onClick={(e) => { e.stopPropagation(); onRemove(m); }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', padding: '0.2rem' }}>
                          <IconSystem.trash size={14} />
                        </button>
                      </td>
                    </tr>
                    {isOpen && <ExpandedMemberRow member={m} colSpan={FANOUT_STAGES.length + 7} groupId={groupId} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ExpandedMemberRow: React.FC<{ member: CaseGroupMember; colSpan: number; groupId: string }> = ({ member, colSpan, groupId }) => {
  const entries = useMemo(() => {
    const sd = member.stateData ?? {};
    return FANOUT_STAGES
      .map(s => ({ stage: s, snap: sd[s.code] }))
      .filter(e => e.snap);
  }, [member.stateData]);

  const handleOpenCase = useCallback(() => {
    if (!member.caseId) {
      notificationService.toast.warning('No case linked to this member.');
      return;
    }
    // Switch to the Litigation Cases tab + pop the case detail modal directly
    // on the Group Participations section with THIS group focused. Case-group
    // modal stays open in its own tab — user navigates back via tabs.
    window.dispatchEvent(new CustomEvent('recsuit:open-screen', {
      detail: { moduleId: 'litigation-case' },
    }));
    window.dispatchEvent(new CustomEvent('recsuit:open-litigation-case-modal', {
      detail: {
        caseId: member.caseId,
        sectionId: 'group-participations',
        focusGroupId: groupId,
      },
    }));
  }, [member.caseId, groupId]);

  return (
    <tr>
      <td colSpan={colSpan} style={{ background: 'var(--bg-secondary)', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
          <span><strong style={{ color: 'var(--text-primary)' }}>Outstanding:</strong> {fmtAmount(member.currentOutstanding)}</span>
          {member.dpd != null && <span><strong style={{ color: 'var(--text-primary)' }}>DPD:</strong> {member.dpd}</span>}
          <span><strong style={{ color: 'var(--text-primary)' }}>Account:</strong> {member.accountNumber || '—'}</span>
          {member.vehicleRegistrationNo && <span><strong style={{ color: 'var(--text-primary)' }}>Vehicle:</strong> {member.vehicleRegistrationNo}</span>}
          {member.parentCaseCode && <span><strong style={{ color: 'var(--text-primary)' }}>From repo case:</strong> {member.parentCaseCode}</span>}
        </div>

        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.35rem' }}>
          Stage entries for this batch
        </div>
        {entries.length === 0 ? (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No stage data yet for this case in this batch.
          </div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8rem', color: 'var(--text-primary)' }}>
            {entries.map(e => (
              <li key={e.stage.code} style={{ marginBottom: '0.2rem' }}>
                <strong>{e.stage.code.replace(/_/g, ' ')}</strong>
                {e.snap!.eventDate && ` — ${fmtDate(e.snap!.eventDate)}`}
                {e.snap!.amountPrimary != null && ` · ${fmtAmount(e.snap!.amountPrimary)}`}
                {e.snap!.statusValue && ` · ${e.snap!.statusValue}`}
                {e.snap!.counterpartyName && ` · ${e.snap!.counterpartyName}`}
                {e.snap!.refMasterName && ` · ${e.snap!.refMasterName}`}
                {e.snap!.submittedByName && <span style={{ color: 'var(--text-muted)' }}> · by {e.snap!.submittedByName}</span>}
              </li>
            ))}
          </ul>
        )}

        {/* Pre-sale recipients (read-only; auto-populated from case parties on first notice save) */}
        <RecipientsBlock dataJson={member.stateData?.PRE_SALE_NOTICE_SENT?.dataJson} />

        <div style={{ marginTop: '0.65rem', display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" style={{ padding: '0.25rem 0.7rem', fontSize: '0.78rem' }} onClick={handleOpenCase}>
            <IconSystem.folder size={13} style={{ marginRight: '0.3rem' }} /> View case in Cases tab
          </button>
        </div>
      </td>
    </tr>
  );
};

const RecipientsBlock: React.FC<{ dataJson?: string | null }> = ({ dataJson }) => {
  const { recipients, noticeNumber } = useMemo<{ recipients: NoticeRecipient[]; noticeNumber?: string }>(() => {
    if (!dataJson) return { recipients: [] };
    try {
      const parsed = JSON.parse(dataJson);
      return {
        recipients: Array.isArray(parsed?.recipients) ? parsed.recipients : [],
        noticeNumber: typeof parsed?.notice_number === 'string' ? parsed.notice_number : undefined,
      };
    } catch { return { recipients: [] }; }
  }, [dataJson]);

  if (recipients.length === 0 && !noticeNumber) return null;

  const statusColor = (s?: string) => {
    if (s === 'DELIVERED') return '#16a34a';
    if (s === 'RETURNED' || s === 'REFUSED' || s === 'NOT_TRACEABLE') return '#dc2626';
    if (s === 'DISPATCHED') return '#2563eb';
    return 'var(--text-muted)';
  };

  return (
    <div style={{ marginTop: '0.85rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.35rem' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700 }}>
          Pre-sale notice recipients
        </div>
        {noticeNumber && (
          <span style={{ fontSize: '0.74rem', color: 'var(--text-primary)' }}>
            <strong>Notice No.</strong> {noticeNumber}
          </span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 4 }}>
        <thead>
          <tr>
            {['Role', 'Name', 'Mode', 'Dispatched', 'Tracking', 'Status', 'Delivered/Returned'].map(h =>
              <th key={h} style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontSize: '0.7rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {recipients.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.recipient_role || '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.recipient_name || '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{(r.mode_of_dispatch || '—').replace(/_/g, ' ')}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.dispatch_date ? fmtDate(r.dispatch_date) : '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.tracking_no || '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)', color: statusColor(r.delivery_status), fontWeight: 600 }}>{r.delivery_status || '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                {r.delivery_date ? fmtDate(r.delivery_date) : '—'}
                {r.return_reason && <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}> · {r.return_reason}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '0.3rem', fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Auto-populated from case parties at notice-batch save. Inline editing of delivery status arrives in the next iteration.
      </div>
    </div>
  );
};

const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', fontSize: '0.72rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)', whiteSpace: 'nowrap' };

// ----------------------------------------------------------------------------
// Pre-Sale Notice fan-out workspace (A4.3)
// ----------------------------------------------------------------------------

interface PreSaleBulkForm {
  notice_date: string;
  auction_date: string;
  cooling_off_days: number;
  letter_reference_prefix: string;
  venue: string;
  reserve_price_basis: string;
  default_mode_of_dispatch: 'SPEED_POST' | 'REGISTERED_POST' | 'COURIER' | 'EMAIL' | 'HAND_DELIVERY';
  notice_status_default: 'DRAFT' | 'DISPATCHED';
}

const INITIAL_BULK: PreSaleBulkForm = {
  notice_date: '',
  auction_date: '',
  cooling_off_days: 30,
  letter_reference_prefix: '',
  venue: '',
  reserve_price_basis: 'VALUATION_PCT_90',
  default_mode_of_dispatch: 'SPEED_POST',
  notice_status_default: 'DISPATCHED',
};

const PreSaleNoticesView: React.FC<{ group: CaseGroup; onSaved: () => void }> = ({ group, onSaved }) => {
  const [bulk, setBulk] = useState<PreSaleBulkForm>({
    ...INITIAL_BULK,
    auction_date: toLocalDateInput(group.eventDate),
    venue: group.venue || '',
  });
  const [selected, setSelected] = useState<Set<string>>(
    new Set(group.members.filter(m => m.participationStatus === 'INCLUDED').map(m => m.caseId))
  );
  const [saving, setSaving] = useState(false);

  // Bulk letter preview state
  const [letters, setLetters] = useState<PreSaleLetter[] | null>(null);
  const [lettersLoading, setLettersLoading] = useState(false);

  const handleGenerateLetters = useCallback(async () => {
    setLettersLoading(true);
    try {
      const res = await caseGroupService.getPreSaleLetters(group.id);
      setLettersLoading(false);
      if (res.length === 0) {
        notificationService.toast.warning('No saved notices found. Save the notice batch first, then generate letters.');
        return;
      }
      setLetters(res);
    } catch (err: any) {
      setLettersLoading(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load letters.', 6000);
    }
  }, [group.id]);

  const includedMembers = useMemo(
    () => group.members.filter(m => m.participationStatus === 'INCLUDED'),
    [group.members]
  );

  const toggleAll = useCallback(() => {
    if (selected.size === includedMembers.length) setSelected(new Set());
    else setSelected(new Set(includedMembers.map(m => m.caseId)));
  }, [selected.size, includedMembers]);

  const toggleOne = useCallback((caseId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(caseId) ? next.delete(caseId) : next.add(caseId);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (selected.size === 0) { notificationService.toast.warning('Select at least one member.'); return; }
    if (!bulk.notice_date) { notificationService.toast.warning('Notice date is required.'); return; }
    if (!bulk.auction_date) { notificationService.toast.warning('Auction date is required.'); return; }

    setSaving(true);
    try {
      const res = await caseGroupService.bulkSaveStage(group.id, {
        stateCode: 'PRE_SALE_NOTICE_SENT',
        commonData: { ...bulk },
        members: Array.from(selected).map(caseId => ({ caseId })),
      });
      setSaving(false);
      notificationService.toast.success(`Notice recorded for ${res.insertedCount} member${res.insertedCount === 1 ? '' : 's'}.`);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save notices.', 6000);
    }
  }, [bulk, selected, group.id, onSaved]);

  const allSelected = selected.size === includedMembers.length && includedMembers.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      {saving && (
        <div style={{
          position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 6,
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving notices…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Pre-Sale Notice</h3>
        <div style={{ display: 'flex', gap: '0.45rem' }}>
          <button className="btn btn-secondary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving || lettersLoading} onClick={handleGenerateLetters}>
            <IconSystem.file size={14} style={{ marginRight: '0.3rem' }} />
            {lettersLoading ? 'Loading…' : 'Generate Letters'}
          </button>
          <button className="btn btn-primary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving} onClick={handleSave}>
            <IconSystem.mail size={14} style={{ marginRight: '0.3rem' }} />
            Save &amp; Send Notices ({selected.size})
          </button>
        </div>
      </div>

      {/* Bulk header */}
      <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.85rem', background: 'var(--bg-secondary)' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.5rem' }}>
          Notice batch — applied to all selected members
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <Field label="Notice Date *" type="date" value={bulk.notice_date}
            onChange={v => setBulk(p => ({ ...p, notice_date: v }))} />
          <Field label="Auction Date *" type="date" value={bulk.auction_date}
            onChange={v => setBulk(p => ({ ...p, auction_date: v }))} />
          <Field label="Notice Period (days)" type="number" value={String(bulk.cooling_off_days)}
            onChange={v => setBulk(p => ({ ...p, cooling_off_days: Number(v) || 30 }))} />
          <Field label="Letter Ref. Prefix" type="text" value={bulk.letter_reference_prefix}
            placeholder="PSN/2026/06/"
            onChange={v => setBulk(p => ({ ...p, letter_reference_prefix: v }))} />
          <Field label="Venue / Platform" type="text" value={bulk.venue}
            onChange={v => setBulk(p => ({ ...p, venue: v }))} />
          <div>
            <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>Default Dispatch Mode</label>
            <select className="form-input" value={bulk.default_mode_of_dispatch}
              onChange={(e) => setBulk(p => ({ ...p, default_mode_of_dispatch: e.target.value as PreSaleBulkForm['default_mode_of_dispatch'] }))}
              style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }}>
              <option value="SPEED_POST">Speed Post</option>
              <option value="REGISTERED_POST">Registered Post</option>
              <option value="COURIER">Courier</option>
              <option value="EMAIL">Email</option>
              <option value="HAND_DELIVERY">Hand Delivery</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Notice number auto-allocates as prefix + 0001, 0002… per group. Default dispatch mode applies to all auto-populated recipients; override per recipient on each ticket.
        </div>
      </div>

      {/* Per-member checklist */}
      {includedMembers.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No included members. Add cases in the Members tab first.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ ...th, width: 40 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Customer</th>
                <th style={th}>Vehicle</th>
                <th style={th}>Outstanding</th>
                <th style={th}>Notice Status</th>
              </tr>
            </thead>
            <tbody>
              {includedMembers.map(m => {
                const existing = m.stateData?.PRE_SALE_NOTICE_SENT;
                const checked = selected.has(m.caseId);
                return (
                  <tr key={m.id} style={{ background: checked ? 'rgba(37,99,235,0.04)' : 'transparent' }}>
                    <td style={cell}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(m.caseId)} />
                    </td>
                    <td style={cell}>{m.seqNo ?? '—'}</td>
                    <td style={cell}><strong>{m.caseCode || '—'}</strong></td>
                    <td style={cell}>{m.customerName || '—'}</td>
                    <td style={cell}>{m.vehicleRegistrationNo || '—'}</td>
                    <td style={cell}>{fmtAmount(m.currentOutstanding)}</td>
                    <td style={cell}>{existing ? stagePill(existing) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.6rem', fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Per-recipient delivery tracking (borrower + guarantor with dispatch/delivery/return) is captured per-member after the notice is saved — open a member row in the Members tab to edit the recipients grid.
      </div>

      {letters && (
        <PreSaleLetterPreview group={group} letters={letters} onClose={() => setLetters(null)} />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Full-screen pre-sale letter preview — paginated, one letter per page,
// print-friendly. Print button calls window.print(); the page break CSS keeps
// each letter on its own sheet.
// ----------------------------------------------------------------------------

const PreSaleLetterPreview: React.FC<{ group: CaseGroup; letters: PreSaleLetter[]; onClose: () => void }> = ({ group, letters, onClose }) => {
  return (
    <div
      data-letter-preview
      style={{
        position: 'fixed', inset: 0, background: '#ffffff', color: '#111',
        zIndex: 1200, overflowY: 'auto',
      }}
    >
      {/* Print-only CSS — hides chrome, separates each letter onto its own page */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-letter-preview], [data-letter-preview] * { visibility: visible !important; }
          [data-letter-preview] { position: absolute !important; inset: 0; overflow: visible !important; }
          [data-letter-preview] .no-print { display: none !important; }
          [data-letter-preview] .letter { page-break-after: always; padding: 0 !important; }
          [data-letter-preview] .letter:last-child { page-break-after: auto; }
          @page { margin: 18mm 16mm; }
        }
      `}</style>

      {/* Toolbar (hidden on print) */}
      <div className="no-print" style={{
        position: 'sticky', top: 0, zIndex: 1,
        background: '#f5f5f7', borderBottom: '1px solid #d1d5db',
        padding: '0.6rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontSize: '0.9rem', color: '#111' }}>
          <strong>{group.groupCode}</strong> · {letters.length} pre-sale notice{letters.length === 1 ? '' : 's'} ready to print
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={onClose} style={{ padding: '0.35rem 0.85rem' }}>
            <IconSystem.x size={14} style={{ marginRight: '0.3rem' }} /> Close
          </button>
          <button className="btn btn-primary" onClick={() => window.print()} style={{ padding: '0.35rem 0.85rem' }}>
            <IconSystem.file size={14} style={{ marginRight: '0.3rem' }} /> Print
          </button>
        </div>
      </div>

      {/* One <Letter> per page */}
      <div style={{ background: '#e9ebef', padding: '1.25rem 0' }}>
        {letters.map((l, i) => (
          <Letter key={`${l.caseId}-${l.recipientRole}-${i}`} letter={l} />
        ))}
      </div>
    </div>
  );
};

const Letter: React.FC<{ letter: PreSaleLetter }> = ({ letter: l }) => {
  const fmtAmt = (n?: number | null) => (n == null ? '' : `Rs. ${Number(n).toLocaleString('en-IN')}`);
  const fmtD = (s?: string | null) => { if (!s) return ''; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return s; } };
  const roleLabel = l.recipientRole === 'BORROWER' ? 'Borrower'
                  : l.recipientRole === 'CO_BORROWER' ? 'Co-Borrower'
                  : l.recipientRole === 'GUARANTOR' ? 'Guarantor'
                  : l.recipientRole;

  return (
    <div className="letter" style={{
      background: '#fff', maxWidth: 780, margin: '0 auto 1.25rem auto',
      padding: '2rem 2.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      fontFamily: '"Segoe UI", "Calibri", Arial, sans-serif', fontSize: '0.95rem', lineHeight: 1.55, color: '#111',
    }}>
      {/* Letterhead strip */}
      <div style={{ borderBottom: '2px solid #111', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.04em' }}>NOTICE OF SALE BY AUCTION</div>
        <div style={{ fontSize: '0.78rem', color: '#444', marginTop: '0.2rem' }}>(Pre-Sale Notice issued under applicable recovery provisions)</div>
      </div>

      {/* Ref + date strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '1rem' }}>
        <div><strong>Notice No.</strong> {l.noticeNumber || '—'}</div>
        <div><strong>Date:</strong> {fmtD(l.noticeDate) || '—'}</div>
      </div>

      {/* Recipient block */}
      <div style={{ marginBottom: '1rem' }}>
        <div>To,</div>
        <div style={{ fontWeight: 700, marginTop: '0.15rem' }}>{l.recipientName} <span style={{ fontWeight: 400, color: '#555', fontSize: '0.82rem' }}>({roleLabel})</span></div>
        {l.recipientAddress && (
          <div style={{ whiteSpace: 'pre-line', marginTop: '0.15rem' }}>{l.recipientAddress}</div>
        )}
        {l.recipientMobile && (
          <div style={{ marginTop: '0.15rem', fontSize: '0.85rem', color: '#444' }}>Mob: {l.recipientMobile}</div>
        )}
      </div>

      <div style={{ marginBottom: '0.85rem' }}>
        <strong>Sub:</strong> Sale of repossessed asset under Loan Account No. <strong>{l.accountNumber || '—'}</strong>
      </div>

      <div style={{ marginBottom: '0.85rem' }}>Dear Sir / Madam,</div>

      <div style={{ marginBottom: '0.85rem', textAlign: 'justify' }}>
        This is to formally notify you that the asset financed against the above loan account (
        {l.vehicleRegistrationNo ? <><strong>Vehicle Reg. No.: {l.vehicleRegistrationNo}</strong>{l.vehicleMakeModel ? <> · {l.vehicleMakeModel}</> : null}</> : 'asset details on file'}
        ) has been lawfully repossessed and is scheduled to be sold by public auction.
      </div>

      <div style={{ margin: '0.85rem 0', padding: '0.55rem 0.85rem', background: '#f5f5f7', border: '1px solid #d1d5db', borderRadius: 4 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: '0.25rem' }}>
          <div><strong>Auction Date:</strong></div><div>{fmtD(l.auctionDate) || '—'}</div>
          <div><strong>Venue / Platform:</strong></div><div>{l.venue || '—'}</div>
          <div><strong>Notice Period:</strong></div><div>{l.coolingOffDays != null ? `${l.coolingOffDays} day(s) from this notice` : '—'}</div>
          {l.outstandingTotal != null && (<><div><strong>Outstanding:</strong></div><div>{fmtAmt(l.outstandingTotal)}</div></>)}
        </div>
      </div>

      <div style={{ marginBottom: '0.85rem', textAlign: 'justify' }}>
        You are hereby called upon to settle the entire outstanding dues, together with applicable interest and charges, within the notice period stated above. Failing this, the asset will be sold and the sale proceeds appropriated towards the outstanding under the loan account. Any shortfall after such appropriation shall remain recoverable from you under applicable law; any surplus shall be refunded as per established procedure.
      </div>

      <div style={{ marginBottom: '0.85rem', textAlign: 'justify' }}>
        You may exercise your right of redemption by clearing the dues before the auction date. Please treat this as a final notice; no further reminder shall be issued.
      </div>

      <div style={{ marginTop: '1.5rem' }}>Yours faithfully,</div>
      <div style={{ marginTop: '2.2rem', fontWeight: 700 }}>Authorised Officer</div>
      <div style={{ fontSize: '0.85rem', color: '#444' }}>(Recovery Cell)</div>

      <div style={{ marginTop: '1.5rem', borderTop: '1px dashed #aaa', paddingTop: '0.4rem', fontSize: '0.7rem', color: '#666' }}>
        Case ref: {l.caseCode}{l.modeOfDispatch ? ` · Dispatched via ${l.modeOfDispatch.replace(/_/g, ' ')}` : ''}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; type: string; value: string; placeholder?: string; onChange: (v: string) => void }> = ({ label, type, value, placeholder, onChange }) => (
  <div>
    <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>{label}</label>
    <input type={type} className="form-input" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }} />
  </div>
);

// ----------------------------------------------------------------------------
// Auction Conduct workspace (A5)
// ----------------------------------------------------------------------------

interface ConductBulk {
  conduct_date: string;
  venue_confirmed: string;
  conducting_officer_name: string;
  conducting_agency_name: string;
  total_bidders: string;
}
interface ConductRow {
  result: 'PENDING' | 'SOLD' | 'UNSOLD' | 'WITHDRAWN';
  reserve_price: string;
  sale_price: string;
  buyer_name: string;
  buyer_contact: string;
  buyer_id_proof: string;
  withdrawal_reason: string;
}

const ConductResultsView: React.FC<{ group: CaseGroup; onSaved: () => void }> = ({ group, onSaved }) => {
  const includedMembers = useMemo(
    () => group.members.filter(m => m.participationStatus === 'INCLUDED'),
    [group.members]
  );

  const [bulk, setBulk] = useState<ConductBulk>({
    conduct_date: toLocalDateInput(group.eventDate),
    venue_confirmed: group.venue || '',
    conducting_officer_name: '',
    conducting_agency_name: '',
    total_bidders: '',
  });

  const [rows, setRows] = useState<Record<string, ConductRow>>(() => {
    const init: Record<string, ConductRow> = {};
    includedMembers.forEach(m => {
      const existing = m.stateData?.AUCTION_CONDUCTED;
      const valued = m.stateData?.VALUATION;
      init[m.caseId] = {
        result: (existing?.statusValue as ConductRow['result']) || 'PENDING',
        reserve_price: existing?.amountSecondary != null ? String(existing.amountSecondary) : (valued?.amountSecondary != null ? String(valued.amountSecondary) : ''),
        sale_price: existing?.amountPrimary != null ? String(existing.amountPrimary) : '',
        buyer_name: existing?.counterpartyName || '',
        buyer_contact: '', buyer_id_proof: '', withdrawal_reason: '',
      };
    });
    return init;
  });

  const [saving, setSaving] = useState(false);

  const update = useCallback((caseId: string, patch: Partial<ConductRow>) => {
    setRows(prev => ({ ...prev, [caseId]: { ...prev[caseId], ...patch } }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!bulk.conduct_date) { notificationService.toast.warning('Conduct date is required.'); return; }
    const toSave = includedMembers.filter(m => rows[m.caseId]?.result && rows[m.caseId].result !== 'PENDING');
    if (toSave.length === 0) { notificationService.toast.warning('Set a result on at least one lot.'); return; }

    setSaving(true);
    try {
      const res = await caseGroupService.bulkSaveStage(group.id, {
        stateCode: 'AUCTION_CONDUCTED',
        commonData: { ...bulk, total_bidders: bulk.total_bidders ? Number(bulk.total_bidders) : null } as any,
        members: toSave.map(m => {
          const r = rows[m.caseId];
          return {
            caseId: m.caseId,
            data: {
              result: r.result,
              reserve_price: r.reserve_price ? Number(r.reserve_price) : null,
              sale_price: r.result === 'SOLD' && r.sale_price ? Number(r.sale_price) : null,
              buyer_name: r.result === 'SOLD' ? r.buyer_name : null,
              buyer_contact: r.result === 'SOLD' ? r.buyer_contact : null,
              buyer_id_proof: r.result === 'SOLD' ? r.buyer_id_proof : null,
              withdrawal_reason: (r.result === 'UNSOLD' || r.result === 'WITHDRAWN') ? r.withdrawal_reason : null,
            },
          };
        }),
      });
      setSaving(false);
      notificationService.toast.success(`Recorded results for ${res.insertedCount} lot${res.insertedCount === 1 ? '' : 's'}.`);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save results.', 6000);
    }
  }, [bulk, rows, includedMembers, group.id, onSaved]);

  return (
    <div style={{ position: 'relative' }}>
      {saving && (
        <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 6 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving results…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Conduct Results</h3>
        <button className="btn btn-primary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving} onClick={handleSave}>
          <IconSystem.check size={14} style={{ marginRight: '0.3rem' }} /> Save Results
        </button>
      </div>

      <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.85rem', background: 'var(--bg-secondary)' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.5rem' }}>
          Conduct details (applies to all lots)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <Field label="Conduct Date *" type="date" value={bulk.conduct_date} onChange={v => setBulk(p => ({ ...p, conduct_date: v }))} />
          <Field label="Venue (confirmed)" type="text" value={bulk.venue_confirmed} onChange={v => setBulk(p => ({ ...p, venue_confirmed: v }))} />
          <Field label="Conducting Officer" type="text" value={bulk.conducting_officer_name} onChange={v => setBulk(p => ({ ...p, conducting_officer_name: v }))} />
          <Field label="Conducting Agency" type="text" value={bulk.conducting_agency_name} onChange={v => setBulk(p => ({ ...p, conducting_agency_name: v }))} />
          <Field label="Total Bidders" type="number" value={bulk.total_bidders} onChange={v => setBulk(p => ({ ...p, total_bidders: v }))} />
        </div>
      </div>

      {includedMembers.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No included members.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Vehicle</th>
                <th style={th}>Result</th>
                <th style={th}>Reserve</th>
                <th style={th}>Sale Price</th>
                <th style={th}>Buyer</th>
                <th style={th}>Contact</th>
              </tr>
            </thead>
            <tbody>
              {includedMembers.map(m => {
                const r = rows[m.caseId];
                if (!r) return null;
                const isSold = r.result === 'SOLD';
                return (
                  <tr key={m.id}>
                    <td style={cell}>{m.seqNo ?? '—'}</td>
                    <td style={cell}><strong>{m.caseCode || '—'}</strong></td>
                    <td style={cell}>{m.vehicleRegistrationNo || '—'}</td>
                    <td style={cell}>
                      <select className="form-input" value={r.result}
                        onChange={(e) => update(m.caseId, { result: e.target.value as ConductRow['result'] })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }}>
                        <option value="PENDING">Pending</option>
                        <option value="SOLD">Sold</option>
                        <option value="UNSOLD">Unsold</option>
                        <option value="WITHDRAWN">Withdrawn</option>
                      </select>
                    </td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.reserve_price}
                        onChange={(e) => update(m.caseId, { reserve_price: e.target.value })}
                        disabled={r.result === 'PENDING' || r.result === 'WITHDRAWN'}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 100 }} />
                    </td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.sale_price}
                        onChange={(e) => update(m.caseId, { sale_price: e.target.value })}
                        disabled={!isSold}
                        placeholder={isSold ? 'Hammer price' : ''}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }} />
                    </td>
                    <td style={cell}>
                      <input type="text" className="form-input" value={r.buyer_name}
                        onChange={(e) => update(m.caseId, { buyer_name: e.target.value })}
                        disabled={!isSold}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 140 }} />
                    </td>
                    <td style={cell}>
                      <input type="text" className="form-input" value={r.buyer_contact}
                        onChange={(e) => update(m.caseId, { buyer_contact: e.target.value })}
                        disabled={!isSold}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 120 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Auction Settlement workspace (A6)
// ----------------------------------------------------------------------------

interface SettleBulk {
  settlement_date: string;
  settlement_batch_ref: string;
  settlement_status: 'POSTED' | 'PENDING';
}
interface SettleRow {
  net_proceeds: string;
  recovery_dues: string;
  outcome_posted: boolean;
  settlement_notes: string;
}

// ----------------------------------------------------------------------------
// Valuation workspace (F8) — per-lot assessed value, reserve, condition.
// ----------------------------------------------------------------------------

interface ValuationBulk {
  valuation_date: string;
  valuator_firm: string;
  valuator_name: string;
  valuator_license_no: string;
}
interface ValuationRow {
  valuation_amount: string;
  reserve_price: string;
  condition: '' | 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'SALVAGE';
  valuation_notes: string;
  valuation_status: 'PENDING' | 'COMPLETED' | 'DISPUTED';
}

const ValuationView: React.FC<{ group: CaseGroup; onSaved: () => void }> = ({ group, onSaved }) => {
  const includedMembers = useMemo(
    () => group.members.filter(m => m.participationStatus === 'INCLUDED'),
    [group.members]
  );

  const [bulk, setBulk] = useState<ValuationBulk>({
    valuation_date: '',
    valuator_firm: '',
    valuator_name: '',
    valuator_license_no: '',
  });

  const [rows, setRows] = useState<Record<string, ValuationRow>>(() => {
    const init: Record<string, ValuationRow> = {};
    includedMembers.forEach(m => {
      const existing = m.stateData?.VALUATION;
      init[m.caseId] = {
        valuation_amount: existing?.amountPrimary != null ? String(existing.amountPrimary) : '',
        reserve_price:    existing?.amountSecondary != null ? String(existing.amountSecondary) : '',
        condition: '' as ValuationRow['condition'],
        valuation_notes: '',
        valuation_status: (existing?.statusValue as ValuationRow['valuation_status']) || 'COMPLETED',
      };
    });
    return init;
  });

  const [saving, setSaving] = useState(false);

  const update = useCallback((caseId: string, patch: Partial<ValuationRow>) => {
    setRows(prev => ({ ...prev, [caseId]: { ...prev[caseId], ...patch } }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!bulk.valuation_date) { notificationService.toast.warning('Valuation date is required.'); return; }
    const toSave = includedMembers.filter(m => rows[m.caseId]?.valuation_amount);
    if (toSave.length === 0) { notificationService.toast.warning('Enter assessed value on at least one lot.'); return; }

    setSaving(true);
    try {
      const res = await caseGroupService.bulkSaveStage(group.id, {
        stateCode: 'VALUATION',
        commonData: { ...bulk } as any,
        members: toSave.map(m => {
          const r = rows[m.caseId];
          return {
            caseId: m.caseId,
            data: {
              valuation_amount: Number(r.valuation_amount),
              reserve_price: r.reserve_price ? Number(r.reserve_price) : null,
              condition: r.condition || null,
              valuation_notes: r.valuation_notes || null,
              valuation_status: r.valuation_status,
            },
          };
        }),
      });
      setSaving(false);
      notificationService.toast.success(`Valuation saved for ${res.insertedCount} lot${res.insertedCount === 1 ? '' : 's'}.`);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save valuation.', 6000);
    }
  }, [bulk, rows, includedMembers, group.id, onSaved]);

  return (
    <div style={{ position: 'relative' }}>
      {saving && (
        <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 6 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving valuation…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Valuation</h3>
        <button className="btn btn-primary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving} onClick={handleSave}>
          <IconSystem.dollarSign size={14} style={{ marginRight: '0.3rem' }} /> Save Valuation
        </button>
      </div>

      <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.85rem', background: 'var(--bg-secondary)' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.5rem' }}>
          Valuation batch (applies to all lots)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <Field label="Valuation Date *" type="date" value={bulk.valuation_date} onChange={v => setBulk(p => ({ ...p, valuation_date: v }))} />
          <Field label="Valuator Firm" type="text" value={bulk.valuator_firm} onChange={v => setBulk(p => ({ ...p, valuator_firm: v }))} />
          <Field label="Valuator Name" type="text" value={bulk.valuator_name} onChange={v => setBulk(p => ({ ...p, valuator_name: v }))} />
          <Field label="License No." type="text" value={bulk.valuator_license_no} onChange={v => setBulk(p => ({ ...p, valuator_license_no: v }))} />
        </div>
      </div>

      {includedMembers.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No included members.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Vehicle</th>
                <th style={th}>Assessed Value</th>
                <th style={th}>Reserve</th>
                <th style={th}>Condition</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {includedMembers.map(m => {
                const r = rows[m.caseId];
                if (!r) return null;
                return (
                  <tr key={m.id}>
                    <td style={cell}>{m.seqNo ?? '—'}</td>
                    <td style={cell}><strong>{m.caseCode || '—'}</strong></td>
                    <td style={cell}>{m.vehicleRegistrationNo || '—'}</td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.valuation_amount}
                        onChange={(e) => update(m.caseId, { valuation_amount: e.target.value })}
                        placeholder="₹"
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 120 }} />
                    </td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.reserve_price}
                        onChange={(e) => update(m.caseId, { reserve_price: e.target.value })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }} />
                    </td>
                    <td style={cell}>
                      <select className="form-input" value={r.condition}
                        onChange={(e) => update(m.caseId, { condition: e.target.value as ValuationRow['condition'] })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }}>
                        <option value="">—</option>
                        <option value="EXCELLENT">Excellent</option>
                        <option value="GOOD">Good</option>
                        <option value="FAIR">Fair</option>
                        <option value="POOR">Poor</option>
                        <option value="SALVAGE">Salvage</option>
                      </select>
                    </td>
                    <td style={cell}>
                      <select className="form-input" value={r.valuation_status}
                        onChange={(e) => update(m.caseId, { valuation_status: e.target.value as ValuationRow['valuation_status'] })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }}>
                        <option value="PENDING">Pending</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="DISPUTED">Disputed</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Newspaper publication workspace (F9) — statutory ad placement.
// ----------------------------------------------------------------------------

interface NewspaperBulk {
  publication_date: string;
  papers: string;
  edition_city: string;
  language: string;
  ad_reference_no: string;
  publication_status: 'SCHEDULED' | 'PUBLISHED' | 'MISSED';
}
interface NewspaperRow {
  included_in_ad: boolean;
  exclusion_reason: string;
}

const NewspaperView: React.FC<{ group: CaseGroup; onSaved: () => void }> = ({ group, onSaved }) => {
  const includedMembers = useMemo(
    () => group.members.filter(m => m.participationStatus === 'INCLUDED'),
    [group.members]
  );

  const [bulk, setBulk] = useState<NewspaperBulk>({
    publication_date: '',
    papers: '',
    edition_city: '',
    language: 'EN',
    ad_reference_no: '',
    publication_status: 'PUBLISHED',
  });

  const [rows, setRows] = useState<Record<string, NewspaperRow>>(() => {
    const init: Record<string, NewspaperRow> = {};
    includedMembers.forEach(m => {
      init[m.caseId] = { included_in_ad: true, exclusion_reason: '' };
    });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const update = useCallback((caseId: string, patch: Partial<NewspaperRow>) => {
    setRows(prev => ({ ...prev, [caseId]: { ...prev[caseId], ...patch } }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!bulk.publication_date) { notificationService.toast.warning('Publication date is required.'); return; }
    if (!bulk.papers.trim())    { notificationService.toast.warning('Enter at least one newspaper.'); return; }

    setSaving(true);
    try {
      const res = await caseGroupService.bulkSaveStage(group.id, {
        stateCode: 'NEWSPAPER_PUBLISHED',
        commonData: { ...bulk } as any,
        members: includedMembers.map(m => {
          const r = rows[m.caseId];
          return {
            caseId: m.caseId,
            data: {
              included_in_ad: r?.included_in_ad ?? true,
              exclusion_reason: r?.included_in_ad === false ? (r.exclusion_reason || null) : null,
            },
          };
        }),
      });
      setSaving(false);
      notificationService.toast.success(`Publication recorded for ${res.insertedCount} lot${res.insertedCount === 1 ? '' : 's'}.`);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save publication.', 6000);
    }
  }, [bulk, rows, includedMembers, group.id, onSaved]);

  return (
    <div style={{ position: 'relative' }}>
      {saving && (
        <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 6 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving publication…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Newspaper Publication</h3>
        <button className="btn btn-primary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving} onClick={handleSave}>
          <IconSystem.file size={14} style={{ marginRight: '0.3rem' }} /> Save Publication
        </button>
      </div>

      <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.85rem', background: 'var(--bg-secondary)' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.5rem' }}>
          Publication details (applies to all lots)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <Field label="Publication Date *" type="date" value={bulk.publication_date} onChange={v => setBulk(p => ({ ...p, publication_date: v }))} />
          <Field label="Edition / City" type="text" value={bulk.edition_city} onChange={v => setBulk(p => ({ ...p, edition_city: v }))} />
          <Field label="Ad Reference No." type="text" value={bulk.ad_reference_no} onChange={v => setBulk(p => ({ ...p, ad_reference_no: v }))} />
          <div>
            <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>Language</label>
            <select className="form-input" value={bulk.language}
              onChange={(e) => setBulk(p => ({ ...p, language: e.target.value }))}
              style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }}>
              <option value="EN">English</option>
              <option value="HI">Hindi</option>
              <option value="KN">Kannada</option>
              <option value="TA">Tamil</option>
              <option value="TE">Telugu</option>
              <option value="MR">Marathi</option>
              <option value="ML">Malayalam</option>
              <option value="MULTI">Multi-language</option>
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>Status</label>
            <select className="form-input" value={bulk.publication_status}
              onChange={(e) => setBulk(p => ({ ...p, publication_status: e.target.value as NewspaperBulk['publication_status'] }))}
              style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }}>
              <option value="SCHEDULED">Scheduled</option>
              <option value="PUBLISHED">Published</option>
              <option value="MISSED">Missed</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '0.6rem' }}>
          <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>Newspapers *</label>
          <textarea className="form-textarea" rows={2} value={bulk.papers}
            placeholder="e.g. Times of India · Deccan Herald · Eenadu (one per line OR comma-separated)"
            onChange={(e) => setBulk(p => ({ ...p, papers: e.target.value }))}
            style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }} />
        </div>
      </div>

      {includedMembers.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No included members.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Vehicle</th>
                <th style={th}>Included in Ad</th>
                <th style={th}>Exclusion Reason</th>
              </tr>
            </thead>
            <tbody>
              {includedMembers.map(m => {
                const r = rows[m.caseId];
                if (!r) return null;
                return (
                  <tr key={m.id}>
                    <td style={cell}>{m.seqNo ?? '—'}</td>
                    <td style={cell}><strong>{m.caseCode || '—'}</strong></td>
                    <td style={cell}>{m.vehicleRegistrationNo || '—'}</td>
                    <td style={cell}>
                      <input type="checkbox" checked={r.included_in_ad}
                        onChange={(e) => update(m.caseId, { included_in_ad: e.target.checked })} />
                    </td>
                    <td style={cell}>
                      <input type="text" className="form-input" value={r.exclusion_reason}
                        disabled={r.included_in_ad}
                        placeholder={r.included_in_ad ? '' : 'Why excluded?'}
                        onChange={(e) => update(m.caseId, { exclusion_reason: e.target.value })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 240 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettlementView: React.FC<{ group: CaseGroup; onSaved: () => void }> = ({ group, onSaved }) => {
  const includedMembers = useMemo(
    () => group.members.filter(m => m.participationStatus === 'INCLUDED'),
    [group.members]
  );

  const [bulk, setBulk] = useState<SettleBulk>({
    settlement_date: '',
    settlement_batch_ref: '',
    settlement_status: 'POSTED',
  });

  const [rows, setRows] = useState<Record<string, SettleRow>>(() => {
    const init: Record<string, SettleRow> = {};
    includedMembers.forEach(m => {
      const conducted = m.stateData?.AUCTION_CONDUCTED;
      const settled = m.stateData?.AUCTION_SETTLED;
      init[m.caseId] = {
        net_proceeds: settled?.amountPrimary != null ? String(settled.amountPrimary + (settled.amountSecondary ?? 0))
                       : conducted?.amountPrimary != null ? String(conducted.amountPrimary) : '',
        recovery_dues: m.currentOutstanding != null ? String(m.currentOutstanding) : '',
        outcome_posted: settled?.flagValue ?? false,
        settlement_notes: '',
      };
    });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const update = useCallback((caseId: string, patch: Partial<SettleRow>) => {
    setRows(prev => ({ ...prev, [caseId]: { ...prev[caseId], ...patch } }));
  }, []);

  const calc = useCallback((r: SettleRow) => {
    const np = Number(r.net_proceeds) || 0;
    const rd = Number(r.recovery_dues) || 0;
    const recovery = Math.min(np, rd);
    const surplus = Math.max(0, np - rd);
    const shortfall = Math.max(0, rd - np);
    return { recovery, surplus, shortfall };
  }, []);

  const handleSave = useCallback(async () => {
    if (!bulk.settlement_date) { notificationService.toast.warning('Settlement date is required.'); return; }
    const toSave = includedMembers.filter(m => {
      const r = rows[m.caseId];
      return r && r.net_proceeds !== '' && r.recovery_dues !== '';
    });
    if (toSave.length === 0) { notificationService.toast.warning('Enter net proceeds + recovery dues on at least one lot.'); return; }

    setSaving(true);
    try {
      const res = await caseGroupService.bulkSaveStage(group.id, {
        stateCode: 'AUCTION_SETTLED',
        commonData: { ...bulk } as any,
        members: toSave.map(m => {
          const r = rows[m.caseId];
          const c = calc(r);
          return {
            caseId: m.caseId,
            data: {
              net_proceeds: Number(r.net_proceeds),
              recovery_dues: Number(r.recovery_dues),
              recovery_amount: c.recovery,
              surplus_amount: c.surplus,
              shortfall_amount: c.shortfall,
              outcome_posted: r.outcome_posted,
              settlement_notes: r.settlement_notes,
            },
          };
        }),
      });
      setSaving(false);
      notificationService.toast.success(`Settlement recorded for ${res.insertedCount} lot${res.insertedCount === 1 ? '' : 's'}.`);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save settlement.', 6000);
    }
  }, [bulk, rows, includedMembers, group.id, calc, onSaved]);

  return (
    <div style={{ position: 'relative' }}>
      {saving && (
        <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 6 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Posting settlement…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Settlement</h3>
        <button className="btn btn-primary" style={{ padding: '0.3rem 0.8rem' }} disabled={saving} onClick={handleSave}>
          <IconSystem.dollarSign size={14} style={{ marginRight: '0.3rem' }} /> Post Settlement
        </button>
      </div>

      <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.85rem', background: 'var(--bg-secondary)' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.5rem' }}>
          Settlement details (applies to all)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <Field label="Settlement Date *" type="date" value={bulk.settlement_date} onChange={v => setBulk(p => ({ ...p, settlement_date: v }))} />
          <Field label="Batch Ref." type="text" value={bulk.settlement_batch_ref} onChange={v => setBulk(p => ({ ...p, settlement_batch_ref: v }))} />
          <div>
            <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem', display: 'block' }}>Status</label>
            <select className="form-input" value={bulk.settlement_status}
              onChange={(e) => setBulk(p => ({ ...p, settlement_status: e.target.value as SettleBulk['settlement_status'] }))}
              style={{ fontSize: '0.82rem', padding: '0.32rem 0.55rem' }}>
              <option value="POSTED">Posted</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {includedMembers.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--form-border-color)', borderRadius: 6 }}>
          No included members.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={th}>Lot</th>
                <th style={th}>Case</th>
                <th style={th}>Net Proceeds</th>
                <th style={th}>Recovery Dues</th>
                <th style={th}>Recovery</th>
                <th style={th}>Surplus</th>
                <th style={th}>Shortfall</th>
                <th style={th}>Posted?</th>
              </tr>
            </thead>
            <tbody>
              {includedMembers.map(m => {
                const r = rows[m.caseId];
                if (!r) return null;
                const c = calc(r);
                return (
                  <tr key={m.id}>
                    <td style={cell}>{m.seqNo ?? '—'}</td>
                    <td style={cell}><strong>{m.caseCode || '—'}</strong></td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.net_proceeds}
                        onChange={(e) => update(m.caseId, { net_proceeds: e.target.value })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }} />
                    </td>
                    <td style={cell}>
                      <input type="number" className="form-input" value={r.recovery_dues}
                        onChange={(e) => update(m.caseId, { recovery_dues: e.target.value })}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem', width: 110 }} />
                    </td>
                    <td style={{ ...cell, fontWeight: 600, color: '#2563eb' }}>{fmtAmount(c.recovery)}</td>
                    <td style={{ ...cell, fontWeight: 600, color: c.surplus > 0 ? '#16a34a' : 'var(--text-muted)' }}>{fmtAmount(c.surplus)}</td>
                    <td style={{ ...cell, fontWeight: 600, color: c.shortfall > 0 ? '#dc2626' : 'var(--text-muted)' }}>{fmtAmount(c.shortfall)}</td>
                    <td style={cell}>
                      <input type="checkbox" checked={r.outcome_posted}
                        onChange={(e) => update(m.caseId, { outcome_posted: e.target.checked })} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.6rem', fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Recovery, Surplus and Shortfall are auto-derived: Recovery = min(Net Proceeds, Recovery Dues). Surplus = Net Proceeds − Dues (refundable). Shortfall = Dues − Net Proceeds (to recover).
      </div>
    </div>
  );
};

const TimelineView: React.FC<{ currentState?: string | null }> = ({ currentState }) => {
  const curIdx = AUCTION_STAGES.findIndex(s => s.code === (currentState || 'DRAFT'));
  return (
    <div>
      <h3 style={{ margin: '0 0 0.85rem', fontSize: '0.95rem' }}>Workflow Timeline</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
        {AUCTION_STAGES.map((s, i) => {
          const done = i < curIdx, current = i === curIdx;
          return (
            <div key={s.code} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0' }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#16a34a' : current ? '#2563eb' : 'transparent',
                border: done || current ? 'none' : '2px solid var(--border-color)',
                color: '#fff', fontSize: '0.65rem',
              }}>{done ? '✓' : ''}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: current ? 700 : 400, color: current ? 'var(--text-primary)' : done ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                {s.label}{current ? '  · current' : ''}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
        Stage advance actions arrive in A5.
      </div>
    </div>
  );
};

const Placeholder: React.FC<{ section: SectionCode; workflowStates?: import('api/types/caseGroup.types').WorkflowStateInfo[] }> = ({ section, workflowStates }) => {
  // Look up the state's panel title from the workflow definition so the
  // placeholder shows the same label as the menu item.
  const state = workflowStates?.find(s => s.stateCode === section);
  const label = state?.panelTitle || state?.displayName || section;
  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
      <IconSystem.info size={28} style={{ opacity: 0.3, marginBottom: '0.6rem' }} />
      <div style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.3rem' }}>{label}</div>
      <div style={{ fontSize: '0.78rem' }}>Workspace UI for this stage is not yet wired — its form_spec is in place, so saved data already appears in the Members tab pills and per-case state-data view.</div>
    </div>
  );
};

const cell: React.CSSProperties = { padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border-color)' };

// ----------------------------------------------------------------------------
// Expenses view — per-group expense lines (lines.case_group_id = thisGroup.id).
// Add Expense pre-fills a GROUP-EQUAL line bound to this group.
// ----------------------------------------------------------------------------

const GroupExpensesView: React.FC<{ group: CaseGroup }> = ({ group }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: rows, loading, error } = useCaseGroupExpenses(group.id, refreshKey);
  const [formOpen, setFormOpen] = useState(false);

  const fmtAmt = (n?: number | null) =>
    n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmt = (s?: string | null) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; } };

  // Roll up at the LINE level (group-level decision) — sum unique expense_line_ids.
  const lines = useMemo(() => {
    const map = new Map<string, ExpenseAllocationView>();
    for (const r of rows) if (!map.has(r.expenseLineId)) map.set(r.expenseLineId, r);
    return Array.from(map.values());
  }, [rows]);
  const total = useMemo(() => lines.reduce((s, l) => s + (l.lineAmount || 0), 0), [lines]);
  const reimb = useMemo(() => lines.filter(l => l.isReimbursable).reduce((s, l) => s + (l.lineAmount || 0), 0), [lines]);

  if (loading && rows.length === 0) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading expenses…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Expenses ({lines.length} line{lines.length === 1 ? '' : 's'})</h3>
        <button className="btn btn-primary" type="button" onClick={() => setFormOpen(true)}
          style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}>
          <IconSystem.add size={14} /> Add Expense
        </button>
      </div>

      {/* Rollups */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        <GxStat label="Total (group)"   value={fmtAmt(total)} />
        <GxStat label="Reimbursable"    value={fmtAmt(reimb)} accent="#16a34a" />
        <GxStat label="Allocations"     value={String(rows.length)} />
      </div>

      {lines.length === 0 ? (
        <div style={{
          padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
          border: '1px dashed var(--border-color)', borderRadius: 6,
        }}>
          <IconSystem.dollarSign size={28} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.9rem' }}>No group-level expenses yet.</div>
          <div style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Click <strong>Add Expense</strong> to record one. Amount will apportion across the {group.members.length} member case(s).
          </div>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
              <tr>
                <th style={gxTh('140px')}>Txn No</th>
                <th style={gxTh('100px')}>Date</th>
                <th style={gxTh('180px')}>Head</th>
                <th style={gxTh()}>Paid To</th>
                <th style={gxTh('120px')}>Mode</th>
                <th style={gxTh('80px')}>Reimb?</th>
                <th style={{ ...gxTh('130px'), textAlign: 'right' }}>Line Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.expenseLineId} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td style={gxTd()}><strong>{l.transactionNo}</strong></td>
                  <td style={gxTd()}>{fmt(l.transactionDate)}</td>
                  <td style={gxTd()}>{l.expenseName || l.expenseCode}</td>
                  <td style={gxTd()}>{l.paidTo || '—'}</td>
                  <td style={gxTd()}>
                    <span style={{
                      fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4,
                      background: '#fef3c7', color: '#92400e',
                    }}>
                      {l.allocationMode}
                    </span>
                  </td>
                  <td style={gxTd()}>
                    {l.isReimbursable
                      ? <span style={{ color: '#16a34a', fontSize: '0.78rem' }}>Yes</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>No</span>}
                  </td>
                  <td style={{ ...gxTd(), textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {fmtAmt(l.lineAmount)}
                  </td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border-color)' }}>
                <td colSpan={6} style={{ ...gxTd(), fontWeight: 600 }}>Total</td>
                <td style={{ ...gxTd(), textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtAmt(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <ExpenseFormModal
        isOpen={formOpen}
        mode="create"
        prefill={{ caseGroupId: group.id }}
        onSaved={() => setRefreshKey(k => k + 1)}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
};

const GxStat: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div style={{
    padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: 6,
    background: 'var(--bg-primary)',
  }}>
    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{
      fontSize: '1.05rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      color: accent || 'var(--text-primary)', marginTop: '0.2rem',
    }}>{value}</div>
  </div>
);

const gxTh = (width?: string): React.CSSProperties => ({
  padding: '0.5rem 0.625rem', textAlign: 'left', fontWeight: 600, fontSize: '0.78rem',
  borderBottom: '2px solid var(--border-color)', whiteSpace: 'nowrap', width,
});
const gxTd = (): React.CSSProperties => ({ padding: '0.45rem 0.625rem', verticalAlign: 'middle' });

export default CaseGroupDetailModal;
